import { useState } from "react";
import type { CSSProperties } from "react";
import { IconProps } from "../../Icon";
import { FieldLabelButton } from "../../FieldLabel/internal/FieldLabelButton";
import fieldLabelButtonStyles from "../../FieldLabel/internal/FieldLabelButton.module.scss";
import { Link } from "../../Link";
import { Tooltip } from "../../Tooltip";
import {
  Popover,
  PopoverDialogRoleProps,
} from "../../../internal/components/Popover";
import { AiMarkIconAnimated } from "./AiMarkIconAnimated";
import { AiMarkWithTooltipOrPopoverConfig } from "../../../types/ai-marks";

import styles from "../AiMark.module.scss";

const DEFAULT_TOOLTIP_CONTENT = "AI can make mistakes.";
const DEFAULT_PRIVACY_HREF =
  "https://www.servicetitan.com/legal/privacy-policy";

function DefaultPopoverContent() {
  return (
    <>
      {DEFAULT_TOOLTIP_CONTENT}{" "}
      <Link
        href={DEFAULT_PRIVACY_HREF}
        target="_blank"
        rel="noopener noreferrer"
        appearance="secondary"
      >
        Learn more
      </Link>
    </>
  );
}

const ICON_PIXEL_SIZES: Record<NonNullable<IconProps["size"]>, number> = {
  small: 12,
  medium: 16,
  large: 24,
  xlarge: 32,
};

/** Minimum outer hit area (px) for the interactive control — accessibility / touch target. */
const FIELD_LABEL_BUTTON_MIN_OUTER_PX = 28;

/**
 * Outer = max(28px, icon + 4px): keeps 2px inset vs the icon where possible, but never below
 * 28×24 default. Inner = outer − 4 (e.g. xlarge 32px icon → 36×32).
 */
function fieldLabelButtonDimensionsForIcon(iconPx: number) {
  const outerPx = Math.max(FIELD_LABEL_BUTTON_MIN_OUTER_PX, iconPx + 4);
  const innerPx = outerPx - 4;
  return { outerPx, innerPx };
}

function fieldLabelButtonScaleStyle(
  outerPx: number,
  innerPx: number,
): CSSProperties {
  return {
    "--field-label-button-outer": `${outerPx}px`,
    "--field-label-button-inner": `${innerPx}px`,
  } as CSSProperties;
}

export type AiMarkInteractiveProps = {
  config: AiMarkWithTooltipOrPopoverConfig;
  size: NonNullable<IconProps["size"]>;
  inheritColor: boolean;
  containerClassName: string;
  forceAnimate?: boolean;
};

export const AiMarkInteractive = ({
  config,
  size,
  inheritColor,
  containerClassName,
  forceAnimate = false,
}: AiMarkInteractiveProps) => {
  const [isActive, setIsActive] = useState(false);

  const iconPixelSize = ICON_PIXEL_SIZES[size];
  const { outerPx, innerPx } = fieldLabelButtonDimensionsForIcon(iconPixelSize);
  const fieldLabelButtonScale = fieldLabelButtonScaleStyle(outerPx, innerPx);

  const animatedIcon = (
    <AiMarkIconAnimated
      animate={forceAnimate || isActive}
      size={iconPixelSize}
      inheritColor={inheritColor}
    />
  );

  const interactionHandlers = {
    onMouseEnter: () => setIsActive(true),
    onMouseLeave: () => setIsActive(false),
    onFocus: () => setIsActive(true),
    onBlur: () => setIsActive(false),
  };

  if (config.type === "popover") {
    const { content, triggerLabel, props, contentProps } = config;
    const popoverContent = content ?? <DefaultPopoverContent />;

    return (
      <div className={containerClassName} data-anv="ai-mark-interactive">
        <Popover role="dialog" {...(props as PopoverDialogRoleProps)}>
          <Popover.Trigger>
            {(triggerProps) => (
              <span className={styles["button-wrapper"]} tabIndex={-1}>
                <FieldLabelButton
                  {...triggerProps}
                  aria-label={triggerLabel}
                  className={
                    fieldLabelButtonStyles["field-label-button-scaled"]
                  }
                  style={fieldLabelButtonScale}
                  onMouseEnter={(e) => {
                    interactionHandlers.onMouseEnter();
                    triggerProps.onMouseEnter?.(e);
                  }}
                  onMouseLeave={(e) => {
                    interactionHandlers.onMouseLeave();
                    triggerProps.onMouseLeave?.(e);
                  }}
                  onFocus={(e) => {
                    interactionHandlers.onFocus();
                    triggerProps.onFocus?.(e);
                  }}
                  onBlur={(e) => {
                    interactionHandlers.onBlur();
                    triggerProps.onBlur?.(e);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    triggerProps.onClick?.(e);
                  }}
                >
                  {animatedIcon}
                </FieldLabelButton>
              </span>
            )}
          </Popover.Trigger>
          <Popover.Content {...contentProps}>{popoverContent}</Popover.Content>
        </Popover>
      </div>
    );
  }

  const { content, triggerLabel, props } = config;
  const tooltipContent = content ?? DEFAULT_TOOLTIP_CONTENT;

  return (
    <div className={containerClassName} data-anv="ai-mark-interactive">
      <Tooltip {...props}>
        <Tooltip.Trigger className={styles["tooltip-trigger"]}>
          <span className={styles["button-wrapper"]} tabIndex={-1}>
            <FieldLabelButton
              aria-label={triggerLabel}
              {...interactionHandlers}
              className={fieldLabelButtonStyles["field-label-button-scaled"]}
              style={fieldLabelButtonScale}
            >
              {animatedIcon}
            </FieldLabelButton>
          </span>
        </Tooltip.Trigger>
        <Tooltip.Content>{tooltipContent}</Tooltip.Content>
      </Tooltip>
    </div>
  );
};
